/**
 * Schémas Zod — registre assureurs (épopée 9.V.*, chunk 9.V.1, ADR 0030).
 *
 * `app.insurers` = entité assureur per-tenant. Identité = UUID `id` (jamais
 * propagée comme clé métier). `code` = clé métier/intégration RENOMMABLE,
 * `UNIQUE(tenant_id, code)`. `provenance` n'est JAMAIS fournie par le client
 * (server-set : 'manual' à la création via API, 'seed'/'migration_backfill'
 * réservés au provisioning/réconciliation SQL).
 *
 * `status` (active/inactive) est piloté par les endpoints dédiés
 * activate/deactivate — PAS par PATCH (évite les transitions ambiguës).
 */

import { z } from "zod";

import { PaginationSchema } from "./common.js";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/**
 * Code assureur = slug d'intégration. Minuscules, chiffres, `.`/`-`/`_`,
 * sans espace (clé stable côté intégrations/exports). 1..60 (miroir check DB
 * `insurers_code_format_chk`). Renommable, mais jamais l'identité (ADR I1).
 */
export const InsurerCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, {
    message: "insurer code must be a lowercase slug (letters, digits, . _ -), no spaces",
  });

export const InsurerStatusSchema = z.enum(["active", "inactive"]);
export type InsurerStatus = z.infer<typeof InsurerStatusSchema>;

// ---------------------------------------------------------------------------
// CREATE — provenance server-set ('manual'), status server-set ('active').
// ---------------------------------------------------------------------------

export const CreateInsurerSchema = z
  .object({
    code: InsurerCodeSchema,
    name: z.string().trim().min(1).max(200),
    short_code: z.string().trim().min(1).max(20).optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type CreateInsurerInput = z.infer<typeof CreateInsurerSchema>;

// ---------------------------------------------------------------------------
// UPDATE — champs éditables uniquement. `status` via activate/deactivate ;
// `provenance` immuable côté API. `.refine` : au moins un champ fourni.
// ---------------------------------------------------------------------------

// Parité `UpdateCompanySchema` : pas de `.refine` at-least-one-field. Un body
// vide `{}` est laissé passer par Zod et rejeté par le handler avec le code
// métier dédié `INSURERS_NO_FIELDS_TO_UPDATE` (400) — wording explicite plutôt
// qu'un `VALIDATION_ERROR` générique.
export const UpdateInsurerSchema = z
  .object({
    code: InsurerCodeSchema.optional(),
    name: z.string().trim().min(1).max(200).optional(),
    // Nullable : permet d'effacer short_code/notes (mettre à null).
    short_code: z.string().trim().min(1).max(20).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .strict();
export type UpdateInsurerInput = z.infer<typeof UpdateInsurerSchema>;

// ---------------------------------------------------------------------------
// LISTING — filtres
// ---------------------------------------------------------------------------

export const InsurerFilterSchema = z.object({
  q: z.string().max(100).optional(),
  status: z.enum(["active", "inactive", "all"]).default("active"),
  provenance: z.enum(["seed", "manual", "migration_backfill"]).optional(),
});
export type InsurerFilter = z.infer<typeof InsurerFilterSchema>;

export const InsurerListQuerySchema = PaginationSchema.merge(InsurerFilterSchema);
export type InsurerListQuery = z.infer<typeof InsurerListQuerySchema>;
